/**
 * Products workspace API (owner only, /api/products). Revenue is in here, so
 * nothing is persisted to localStorage (meta.persist false). apiClient
 * already returns the response body.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiClient } from "@/lib/api"
import type { Product, ProductDetail, ProductOptions, ProductOverviewRow, ProductRange } from "@/lib/products"

export type ProductSettingsUpdate = Partial<
  Pick<Product, "name" | "domain" | "gsc_property" | "ga4_property" | "posthog_project_id" | "events_project" | "signup_events" | "lead_events" | "sort_order">
>

export const productsApi = {
  list: (days: ProductRange) => apiClient.get<{ data: ProductOverviewRow[] }>("/products", { params: { days } }).then((b) => b.data),
  show: (slug: string, days: ProductRange) => apiClient.get<{ data: ProductDetail }>(`/products/${slug}`, { params: { days } }).then((b) => b.data),
  options: () => apiClient.get<{ data: ProductOptions }>("/products/options").then((b) => b.data),
  create: (body: ProductSettingsUpdate & { name: string; slug: string }) => apiClient.post<{ data: Product }>("/products", body).then((b) => b.data),
  update: (slug: string, body: ProductSettingsUpdate) => apiClient.patch<{ data: Product }>(`/products/${slug}`, body).then((b) => b.data),
  refresh: (slug: string) => apiClient.post(`/products/${slug}/refresh`),
  putStripeKey: (slug: string, key: string) => apiClient.put<{ data: Product }>(`/products/${slug}/stripe-key`, { key }).then((b) => b.data),
  deleteStripeKey: (slug: string) => apiClient.delete<{ data: Product }>(`/products/${slug}/stripe-key`).then((b) => b.data),
}

export const productKeys = {
  all: ["products"] as const,
  list: (days: number) => ["products", "list", days] as const,
  detail: (slug: string, days: number) => ["products", "detail", slug, days] as const,
  options: ["products", "options"] as const,
}

/** The snapshot is collected every 15 minutes; check for a new one every minute while the page is open. */
const POLL_MS = 60_000

export function useProducts(days: ProductRange, enabled = true) {
  return useQuery({ queryKey: productKeys.list(days), queryFn: () => productsApi.list(days), enabled, refetchInterval: POLL_MS, meta: { persist: false } })
}

export function useProduct(slug: string, days: ProductRange) {
  return useQuery({ queryKey: productKeys.detail(slug, days), queryFn: () => productsApi.show(slug, days), refetchInterval: POLL_MS, meta: { persist: false } })
}

export function useProductOptions(enabled: boolean) {
  return useQuery({ queryKey: productKeys.options, queryFn: productsApi.options, enabled, staleTime: 10 * 60_000, meta: { persist: false } })
}

export function useProductMutations(slug: string) {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: productKeys.all })
  return {
    update: useMutation({ mutationFn: (body: ProductSettingsUpdate) => productsApi.update(slug, body), onSuccess: invalidate }),
    putStripeKey: useMutation({ mutationFn: (key: string) => productsApi.putStripeKey(slug, key), onSuccess: invalidate }),
    deleteStripeKey: useMutation({ mutationFn: () => productsApi.deleteStripeKey(slug), onSuccess: invalidate }),
    refresh: useMutation({
      mutationFn: () => productsApi.refresh(slug),
      // The job reads five vendors; look again once it has had time to land.
      onSuccess: () => {
        setTimeout(invalidate, 20_000)
        setTimeout(invalidate, 60_000)
      },
    }),
  }
}
